import React from 'react';

import { PageContainer } from '../../components/PageContainer';
import { Outlet, Route } from 'react-router-dom';
import { HowToIndex } from '.';
import Stack from '@mui/material/Stack/Stack';
import { AppBreadcrumbs, PageType } from '../../components/Breadcrumbs/Buttons';
import Box from '@mui/material/Box/Box';
import { Theme } from '@mui/material/styles';
import { GuideMenu, GuidePager, GuidePlace, GuideSidebar } from './GuideNav';


/** Articles */
import startHere from './start_here.md';
import createHackathon from './create_hackathon.md';
import categories from './categories.md';
import scoring from './scoring.md';
import invitePeople from './invite_people.md';
import judges from './judges.md';
import facilitatePresentations from './facilitate_presentations.md';
import rooms from './rooms.md';
import announceWinners from './announce_winners.md';
import join from './join.md';
import submitIdea from './submit_idea.md';
import castVote from './cast_vote.md';
import scoreParticipant from './score_participant.md';
import organizations from './organizations.md';

// each guide page brings its own PageContainer; wrapping the outlet in another one nested a card inside a card
export const HowTo = () => <Outlet />;

// markdown puts each image in its own paragraph; make it a captioned figure, captioned from its alt text
const asFigures = (html: string) => html.replace(/<p>\s*(<img[^>]*?alt="([^"]*)"[^>]*>)\s*<\/p>/g,
    (_, img, alt) => `<figure>${img}<figcaption>Screenshot${alt ? `: ${alt}` : ''}</figcaption></figure>`);

export const HowToContainer = ({
    name,
    link,
    article
}: {
    name: string,
    link: string,
    article: string
}) => {
    // a new guide starts at its top, not wherever the last one was scrolled to
    React.useEffect(() => { window.scrollTo(0, 0); }, [link]);
    return (
        <PageContainer>
            <Stack direction="column" spacing={2}>
                <AppBreadcrumbs crumbs={
                    [
                        {
                            type: PageType.Home,
                            link: '/',
                            name: 'Home'
                        },
                        {
                            type: PageType.HowTo,
                            link: '/how/to',
                            name: 'How To'
                        },
                        {
                            type: PageType.Article,
                            name: name
                        }
                    ]
                } />
                <Stack direction="row" spacing={{ md: 4 }}>
                    <GuideSidebar guides={HowToArticles} current={link} />
                    <Stack direction="column" spacing={2} useFlexGap sx={{ flex: 1, minWidth: 0 }}>
                        <GuideMenu guides={HowToArticles} current={link} />
                        <GuidePlace guides={HowToArticles} current={link} />
                        {/* the articles are plain HTML from markdown; give them reading rhythm */}
                        <Box sx={{
                            maxWidth: 760, lineHeight: 1.6,
                            '& h1': { mt: 0, mb: 1 }, '& h2': { mt: 4, mb: 1 }, '& h3': { mt: 3, mb: 0.5 },
                            '& p, & ul, & ol, & blockquote': { mt: 0, mb: 1.5 }, '& li': { mb: 0.5 },
                            // a quote is a soft tinted panel, not a bar down the side
                            '& blockquote': { backgroundColor: 'var(--surface-muted)', borderRadius: '12px', px: 2.5, py: 1.5, mx: 0, color: 'text.secondary' },
                            '& blockquote > :last-child': { mb: 0 },
                            // codes and addresses people type (an invite code, a domain), set like terminal input
                            '& code': { fontFamily: (theme: Theme) => theme.brand.mono, fontSize: '0.9em', backgroundColor: 'var(--surface-muted)', borderRadius: '6px', px: 0.75, py: 0.25 },
                            // screenshots of the app: framed, so they read as pictures rather than live controls
                            // screenshots sit on a tinted panel, a size smaller than the text, with a caption, so their own text
                            // can't be mistaken for the guide's
                            '& figure': {
                                mx: 0, my: 3, p: { xs: 1.5, sm: 3 }, textAlign: 'center',
                                backgroundColor: 'var(--surface-muted)', borderRadius: '14px'
                            },
                            '& figure img': {
                                display: 'block', mx: 'auto', maxWidth: { xs: '100%', sm: '85%' }, height: 'auto',
                                backgroundColor: 'background.paper', borderRadius: '8px',
                                border: '1px solid var(--border-color)', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', pointerEvents: 'none'
                            },
                            '& figcaption': { mt: 1.5, fontSize: '0.8125rem', color: 'text.secondary' }
                        }} dangerouslySetInnerHTML={{ __html: asFigures(article) }} />
                        <GuidePager guides={HowToArticles} current={link} />
                    </Stack>
                </Stack>
            </Stack>
        </PageContainer>
    )
}

// in the order an event runs; the index groups them by who they're for
export const HowToArticles = [
    { title: 'Run your first hackathon', link: 'start', audience: 'Start here', summary: 'Seven steps, from setup to winners.', article: startHere },
    { title: 'Create a hackathon', link: 'create', audience: 'Organizers', summary: 'From scratch or from a past event.', article: createHackathon },
    { title: 'Set up your company', link: 'organizations', audience: 'Organizers', summary: 'Access rule, branding and admins for all your events.', article: organizations },
    { title: 'Set up judging', link: 'categories', audience: 'Organizers', summary: 'Categories, and who scores.', article: categories },
    { title: 'Invite participants', link: 'invite', audience: 'Organizers', summary: 'Send the link and invite code.', article: invitePeople },
    { title: 'Add judges', link: 'judges', audience: 'Organizers', summary: 'A judge link, or by email.', article: judges },
    { title: 'Run the presentations', link: 'present', audience: 'Organizers', summary: 'The stage, the order, and voting.', article: facilitatePresentations },
    { title: 'Several rooms', link: 'rooms', audience: 'Organizers', summary: 'When teams present in more than one place.', article: rooms },
    { title: 'Announce the winners', link: 'winners', audience: 'Organizers', summary: 'Close voting, pick winners, share results.', article: announceWinners },
    { title: 'Join a hackathon', link: 'join', audience: 'Participants', summary: 'Sign in and enter the invite code.', article: join },
    { title: 'Pitch an idea or join a team', link: 'submit', audience: 'Participants', summary: 'Teams, and keeping your idea current.', article: submitIdea },
    { title: 'Score other teams', link: 'score', audience: 'Participants', summary: 'When participants score too.', article: scoreParticipant },
    { title: 'Score as a judge', link: 'vote', audience: 'Judges', summary: 'The Judging page, step by step.', article: castVote },
    { title: 'How scores are counted', link: 'scoring', audience: 'Judges', summary: 'Averages, totals and ranks.', article: scoring },
]

// people guess a guide's address from its title ("/how/to/run-your-first-hackathon"); answer at that one too
export const titleSlug = (title: string) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const HowToRoutes = (
    <Route element={<HowTo />}>
        <Route path="/how/to" element={<HowToIndex />} />
        {
            HowToArticles.map((article, index) => {
                return (
                    <React.Fragment key={index}>
                        <Route path={`/how/to/${article.link}`} element={<HowToContainer name={article.title} link={article.link} article={article.article} />} />
                        {titleSlug(article.title) !== article.link &&
                            <Route path={`/how/to/${titleSlug(article.title)}`} element={<HowToContainer name={article.title} link={article.link} article={article.article} />} />}
                    </React.Fragment>
                )
            })
        }
        {/* a mistyped guide address lands on the list of guides, not a dead end */}
        <Route path="/how/to/*" element={<HowToIndex notFound />} />
    </Route>
)



